import React from "react";
import { useTranslation } from "react-i18next";
import type { Source } from "../../types";
import { LawIcon, ExternalLinkIcon } from "./Icons.tsx";

interface SourceListProps {
  sources: Source[];
}

export const SourceList: React.FC<SourceListProps> = ({ sources }) => {
  const { t } = useTranslation();
  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-10 pt-8 border-t border-slate-200/50 dark:border-slate-800/50">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
            <LawIcon className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{t("common.sources_title", "Kasutatud õigusallikad")}</h3>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{t("common.sources_subtitle", "Riigi Teataja valideeritud andmed")}</p>
          </div>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold border border-emerald-500/20 shadow-sm animate-pulse">
          {t("common.sources_latest", "Uusimad andmed")}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sources.map((src, i) => (
          <a
            key={i}
            href={src.url}
            target="_blank"
            rel="noreferrer"
            className="group/source relative flex flex-col p-4 rounded-2xl bg-white/50 dark:bg-slate-800/30 border border-slate-200/60 dark:border-slate-700/60 hover:border-blue-400/50 dark:hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 overflow-hidden animate-in-stagger"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="flex items-start justify-between gap-4 mb-3 relative z-10">
              <div className="flex items-start gap-3">
                <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[11px] font-black border border-blue-500/20 group-hover/source:bg-blue-600 group-hover/source:text-white transition-all duration-300">
                  {i + 1}
                </span>
                <span className="text-sm font-bold text-slate-900 dark:text-white group-hover/source:text-blue-600 dark:group-hover/source:text-blue-400 transition-colors line-clamp-2 leading-snug">
                  {src.title}
                </span>
              </div>
              <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 group-hover/source:bg-blue-600 group-hover/source:text-white transition-all shadow-sm shrink-0">
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </div>
            </div>

            {src.citation && (
              <div className="mt-auto relative z-10">
                <span className="inline-block text-[10px] font-bold px-3 py-1.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/10 shadow-sm">
                  {src.citation}
                </span>
              </div>
            )}
          </a>
        ))}
      </div>
    </div>
  );
};
